import React, { useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import globalUserPage from '../../../../../stores/global-user-page';
import { observer } from 'mobx-react-lite';
import { formatDate } from '../../../../../utils/date-format';
import { HeaderCards } from './components/header-cards';
import { InstallmentsTable } from './components/installments-table';
import {
  InstallmentWithPlanItemsDetails,
  PaymentInstallmentStatus,
} from '../../../../../api/types/payments/installments/entities';
import { UserSummaryType } from '../../../../../api/types/user/user-summary.type';
import { CardBrandType } from '../../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import { SwitchCreditCardDialog } from './components/switch-credit-card-dialog/switch-credit-card-dialog';
import { SwitchBranchDialog } from './components/switch-branch-dialog/switch-branch-dialog';
import { AddNewCardDialog } from '../../payment-methods/components/add-new-card-dialog';
import { updateInstallmentCreditCardOperation } from './operations/update-installment-credit-card-operation';
import { updateInstallmentBranchOperation } from './operations/update-installment-branch-operation';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';

export interface UserPageStudentInstallmentsDatamodel {
  user: UserSummaryType;
}

// Helper function to get card brand display name
const getCardBrandDisplayName = (brand: CardBrandType): string => {
  const brandNames: Record<CardBrandType, string> = {
    [CardBrandType.VISA]: 'ויזה',
    [CardBrandType.MASTERCARD]: 'מאסטרקארד',
    [CardBrandType.AMERICAN_EXPRESS]: 'אמריקן אקספרס',
    [CardBrandType.ISRACARD]: 'ישראכרט',
    [CardBrandType.DINERS_CLUB]: 'דיינרס קלאב',
    [CardBrandType.DISCOVER]: 'דיסקובר',
    [CardBrandType.MAESTRO]: 'מאסטרו',
    [CardBrandType.JCB]: 'JCB',
    [CardBrandType.PRIVATE_LABEL]: 'כרטיס פרטי',
  };
  return brandNames[brand] || brand;
};

// Helper function to get card brand icon
const getCardBrandIcon = (brand: CardBrandType): React.ReactNode => {
  const iconStyle = { width: '32px', height: '20px', marginLeft: '8px' };

  switch (brand) {
    case CardBrandType.VISA:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#1A1F71" />
          <path
            d="M19.5 21H17L18.8 11H21.3L19.5 21ZM15.2 11L12.8 18L12.5 16.5L11.6 12.1C11.6 12.1 11.5 11 10 11H6.1L6 11.2C6 11.2 7.7 11.5 9.7 12.7L11.8 21H14.4L18 11H15.2ZM35.3 21H37.5L35.6 11H33.6C32.4 11 32.1 11.9 32.1 11.9L28.2 21H30.8L31.3 19.5H34.5L34.8 21H35.3ZM32.1 17.5L33.5 13.8L34.3 17.5H32.1ZM27.8 13.7L28.2 11.3C28.2 11.3 26.8 10.8 25.3 10.8C23.7 10.8 20.8 11.5 20.8 14.1C20.8 16.5 24.2 16.5 24.2 17.8C24.2 19.1 21.2 18.7 19.9 17.7L19.5 20.2C19.5 20.2 20.9 20.8 23 20.8C25.1 20.8 27.9 19.6 27.9 17.2C27.9 14.7 24.5 14.5 24.5 13.4C24.5 12.3 26.7 12.5 27.8 13.7Z"
            fill="white"
          />
        </svg>
      );
    case CardBrandType.MASTERCARD:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#F5F5F5" />
          <circle cx="18" cy="16" r="9" fill="#EB001B" />
          <circle cx="30" cy="16" r="9" fill="#F79E1B" />
          <path
            d="M24 9.5C26.2 11.2 27.5 13.4 27.5 16C27.5 18.6 26.2 20.8 24 22.5C21.8 20.8 20.5 18.6 20.5 16C20.5 13.4 21.8 11.2 24 9.5Z"
            fill="#FF5F00"
          />
        </svg>
      );
    case CardBrandType.AMERICAN_EXPRESS:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#006FCF" />
          <path
            d="M8 16L10 12H12L14 16L16 12H18L15 18H13L11 14L9 18H7L8 16ZM18 12H24L25 14H21V15H24V16H21V17H25L24 19H18V12ZM27 12L29 16L31 12H33L30 18H28L25 12H27ZM33 12H39L40 14H36V15H39V16H36V17H40L39 19H33V12Z"
            fill="white"
          />
        </svg>
      );
    case CardBrandType.ISRACARD:
      return (
        <svg style={iconStyle} viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#003087" />
          <text
            x="24"
            y="19"
            textAnchor="middle"
            fill="white"
            fontSize="10"
            fontWeight="bold"
          >
            ישראכרט
          </text>
        </svg>
      );
    default:
      return (
        <i
          className="pi pi-credit-card"
          style={{ fontSize: '20px', marginLeft: '8px', color: '#666' }}
        />
      );
  }
};

export const UserPageStudentInstallments: React.FC<UserPageStudentInstallmentsDatamodel> =
  observer(({ user }) => {
    const [activeIndex, setActiveIndex] = useState<number | number[] | null>(
      null,
    );

    // Switch credit card dialog state
    const [showSwitchCardDialog, setShowSwitchCardDialog] = useState(false);
    const [switchCardLoading, setSwitchCardLoading] = useState(false);
    const [selectedPlanForSwitch, setSelectedPlanForSwitch] =
      useState<InstallmentWithPlanItemsDetails | null>(null);

    // Add new card dialog state
    const [showAddCardDialog, setShowAddCardDialog] = useState(false);

    // Switch branch dialog state
    const [showSwitchBranchDialog, setShowSwitchBranchDialog] = useState(false);
    const [switchBranchLoading, setSwitchBranchLoading] = useState(false);
    const [selectedPlanForBranchSwitch, setSelectedPlanForBranchSwitch] =
      useState<InstallmentWithPlanItemsDetails | null>(null);

    const installments = useMemo(() => {
      return globalUserPage.studentDetails?.installments ?? [];
    }, [globalUserPage.studentDetails?.installments]);

    const creditCardTokens = useMemo(() => {
      return globalUserPage.studentDetails?.creditCardTokens ?? [];
    }, [globalUserPage.studentDetails?.creditCardTokens]);

    // Flatten all installments from all plans for summary calculation
    const allInstallmentItems = useMemo(() => {
      return installments.flatMap((installmentPlan) =>
        installmentPlan.installment.map((item) => ({
          ...item,
          planTotalAmount: installmentPlan.installmentPlan.totalAmount,
          planNumberOfInstallments:
            installmentPlan.installmentPlan.numberOfInstallments,
          planFirstPaymentDate:
            installmentPlan.installmentPlan.firstPaymentDate,
          planPaymentDayOfMonth:
            installmentPlan.installmentPlan.paymentDayOfMonth,
          planStatus: installmentPlan.installmentPlan.status,
        })),
      );
    }, [installments]);

    // Calculate summary statistics
    const summary = useMemo(() => {
      const totalPlans = installments.length;
      const totalAmount = installments.reduce(
        (sum, plan) => sum + plan.installmentPlan.totalAmount,
        0,
      );
      const paidAmount = allInstallmentItems
        .filter((item) => item.status === PaymentInstallmentStatus.PAID)
        .reduce((sum, item) => sum + item.amount, 0);
      const pendingAmount = allInstallmentItems
        .filter((item) => item.status === PaymentInstallmentStatus.PENDING)
        .reduce((sum, item) => sum + item.amount, 0);
      const paidCount = allInstallmentItems.filter(
        (item) => item.status === PaymentInstallmentStatus.PAID,
      ).length;
      const totalInstallments = allInstallmentItems.length;

      return {
        totalPlans,

        totalAmount,
        paidAmount,
        pendingAmount,
        paidCount,
        totalInstallments,
      };
    }, [installments, allInstallmentItems]);

    // Calculate stats for each plan
    const getPlanStats = (plan: InstallmentWithPlanItemsDetails) => {
      const paidCount = plan.installment.filter(
        (item) => item.status === PaymentInstallmentStatus.PAID,
      ).length;
      const failedCount = plan.installment.filter(
        (item) => item.status === PaymentInstallmentStatus.FAILED,
      ).length;
      const paymentServicesCount =
        plan.paymentInstallmentPlanItemAllocations.length;

      return {
        paidCount,
        failedCount,
        paymentServicesCount,
      };
    };

    // Handle switch credit card click
    const handleSwitchCreditCardClick = (
      plan: InstallmentWithPlanItemsDetails,
      e: React.MouseEvent,
    ) => {
      e.stopPropagation();
      setSelectedPlanForSwitch(plan);
      setShowSwitchCardDialog(true);
    };

    // Handle switch credit card confirm
    const handleSwitchCreditCardConfirm = async (creditCardTokenId: number) => {
      if (!selectedPlanForSwitch) return;

      await updateInstallmentCreditCardOperation(
        user,
        selectedPlanForSwitch.installmentPlan.id,
        creditCardTokenId,
        setSwitchCardLoading,
        () => {
          setShowSwitchCardDialog(false);
          setSelectedPlanForSwitch(null);
        },
      );
    };

    // Handle add new card click (from switch dialog)
    const handleAddNewCardFromDialog = () => {
      setShowSwitchCardDialog(false);
      setShowAddCardDialog(true);
    };

    // Handle add new card success
    const handleAddNewCardSuccess = () => {
      setShowAddCardDialog(false);
      // Re-open switch dialog after adding card if we came from there
      if (selectedPlanForSwitch) {
        setShowSwitchCardDialog(true);
      }
    };

    // Handle switch branch click
    const handleSwitchBranchClick = (
      plan: InstallmentWithPlanItemsDetails,
      e: React.MouseEvent,
    ) => {
      e.stopPropagation();
      setSelectedPlanForBranchSwitch(plan);
      setShowSwitchBranchDialog(true);
    };

    // Handle switch branch confirm
    const handleSwitchBranchConfirm = async (branchId: number) => {
      if (!selectedPlanForBranchSwitch) return;

      await updateInstallmentBranchOperation(
        user,
        selectedPlanForBranchSwitch.installmentPlan.id,
        branchId,
        setSwitchBranchLoading,
        () => {
          setShowSwitchBranchDialog(false);
          setSelectedPlanForBranchSwitch(null);
        },
      );
    };

    // Render the header for each accordion tab
    const renderAccordionHeader = (plan: InstallmentWithPlanItemsDetails) => {
      const stats = getPlanStats(plan);
      const createdAt = plan.installmentPlan.createdAt;
      // Look up credit card token by ID from creditCardTokens array
      const creditCard = plan.installmentPlan.creditCardTokenId
        ? (creditCardTokens.find(
            (token) => token.id === plan.installmentPlan.creditCardTokenId,
          ) ?? null)
        : null;
      // Look up the plan's branch name (if any) from the global branches store
      const planBranchName = plan.installmentPlan.branchId
        ? (globalSettingsBranches.branches.find(
            (b) => b.id === plan.installmentPlan.branchId,
          )?.branchName ?? null)
        : null;

      return (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '32px',
            alignItems: 'center',
            width: '100%',
            padding: '12px 0',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              נוצר בתאריך
            </span>
            <span
              style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '18px' }}
            >
              {formatDate(createdAt)}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              סכום כולל
            </span>
            <span
              style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '18px' }}
            >
              ₪{plan.installmentPlan.totalAmount.toLocaleString()}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              מספר תשלומים
            </span>
            <span
              style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '18px' }}
            >
              {plan.installmentPlan.numberOfInstallments}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              שולמו בהצלחה
            </span>
            <span
              style={{ fontWeight: 'bold', color: '#22c55e', fontSize: '18px' }}
            >
              {stats.paidCount}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>נכשלו</span>
            <span
              style={{
                fontWeight: 'bold',
                color: stats.failedCount > 0 ? '#ef4444' : '#6b7280',
                fontSize: '18px',
              }}
            >
              {stats.failedCount}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              מספר שירותי תשלום
            </span>
            <span
              style={{ fontWeight: 'bold', color: '#1f2937', fontSize: '18px' }}
            >
              {stats.paymentServicesCount}
            </span>
          </div>

          {/* Branch Info */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              marginRight: 'auto',
            }}
          >
            <span style={{ fontSize: '16px', color: '#6b7280' }}>שלוחה</span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {planBranchName ? (
                <span
                  style={{
                    fontWeight: 'bold',
                    color: '#1f2937',
                    fontSize: '16px',
                  }}
                >
                  {planBranchName}
                </span>
              ) : (
                <span style={{ color: '#9CA3AF', fontSize: '14px' }}>
                  לא הוגדרה שלוחה
                </span>
              )}
              <Button
                icon="pi pi-pencil"
                size="small"
                rounded
                text
                severity="warning"
                tooltip="שינוי שלוחה"
                tooltipOptions={{ position: 'top' }}
                onClick={(e) => handleSwitchBranchClick(plan, e)}
              />
            </div>
          </div>

          {/* Credit Card Info */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <span style={{ fontSize: '16px', color: '#6b7280' }}>
              כרטיס אשראי
            </span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {creditCard ? (
                <>
                  {getCardBrandIcon(creditCard.cardBrand)}
                  <span
                    style={{
                      fontWeight: 'bold',
                      color: '#1f2937',
                      fontSize: '16px',
                    }}
                  >
                    {getCardBrandDisplayName(creditCard.cardBrand)} **** ****
                    **** {creditCard.cardLast4}
                  </span>
                </>
              ) : (
                <span style={{ color: '#9CA3AF', fontSize: '14px' }}>
                  לא הוגדר כרטיס
                </span>
              )}
              <Button
                icon="pi pi-sync"
                size="small"
                rounded
                text
                severity="info"
                tooltip="החלף כרטיס"
                tooltipOptions={{ position: 'top' }}
                onClick={(e) => handleSwitchCreditCardClick(plan, e)}
                style={{ marginRight: '4px' }}
              />
              <Button
                icon="pi pi-plus"
                size="small"
                rounded
                text
                severity="success"
                tooltip="הוסף כרטיס חדש"
                tooltipOptions={{ position: 'top' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPlanForSwitch(plan);
                  setShowAddCardDialog(true);
                }}
              />
            </div>
          </div>
        </div>
      );
    };

    if (installments.length === 0) {
      return (
        <div style={{ width: '100%', padding: '24px' }}>
          <Card>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '48px',
                gap: '16px',
              }}
            >
              <i
                className="pi pi-calendar"
                style={{ fontSize: '48px', color: '#9ca3af' }}
              />
              <h3 style={{ margin: 0, color: '#4b5563' }}>
                אין הוראות קבע באשראי
              </h3>
              <p style={{ margin: 0, color: '#6b7280', textAlign: 'center' }}>
                לא נמצאו הוראות קבע באשראי עבור משתמש זה.
              </p>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <div style={{ width: '100%', padding: '16px' }}>
        {/* Switch Credit Card Dialog */}
        <SwitchCreditCardDialog
          visible={showSwitchCardDialog}
          onHide={() => {
            setShowSwitchCardDialog(false);
            setSelectedPlanForSwitch(null);
          }}
          creditCardTokens={creditCardTokens}
          currentCreditCardTokenId={
            selectedPlanForSwitch?.installmentPlan.creditCardTokenId ?? null
          }
          onConfirm={handleSwitchCreditCardConfirm}
          onAddNewCard={handleAddNewCardFromDialog}
          loading={switchCardLoading}
        />

        {/* Add New Card Dialog */}
        <AddNewCardDialog
          visible={showAddCardDialog}
          onHide={() => {
            setShowAddCardDialog(false);
            if (!showSwitchCardDialog) {
              setSelectedPlanForSwitch(null);
            }
          }}
          userId={user.id}
          onSuccess={handleAddNewCardSuccess}
        />

        {/* Switch Branch Dialog */}
        <SwitchBranchDialog
          visible={showSwitchBranchDialog}
          onHide={() => {
            if (!switchBranchLoading) {
              setShowSwitchBranchDialog(false);
              setSelectedPlanForBranchSwitch(null);
            }
          }}
          currentBranchId={
            selectedPlanForBranchSwitch?.installmentPlan.branchId ?? null
          }
          onConfirm={handleSwitchBranchConfirm}
          loading={switchBranchLoading}
        />

        {/* Summary Cards */}
        <HeaderCards summary={summary} />

        {/* Installment Plans Accordion */}
        <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#1f2937' }}>
          הוראות קבע
        </h3>
        <Accordion
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index)}
          multiple
        >
          {installments.map((plan, index) => (
            <AccordionTab
              key={plan.installmentPlan.id}
              header={renderAccordionHeader(plan)}
              headerStyle={{
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                marginBottom: '8px',
              }}
              contentStyle={{
                padding: '8px',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '0 0 8px 8px',
              }}
            >
              <InstallmentsTable user={user} plan={plan} />
            </AccordionTab>
          ))}
        </Accordion>

        <style>{`
        .p-accordion .p-accordion-header .p-accordion-header-link {
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }
        
        .p-accordion .p-accordion-header:not(.p-disabled).p-highlight .p-accordion-header-link {
          background-color: #f3f4f6;
          border-color: #d1d5db;
        }
        
        .p-accordion .p-accordion-header:not(.p-disabled) .p-accordion-header-link:hover {
          background-color: #f3f4f6;
        }
        
        .p-accordion .p-accordion-content {
          border: none;
        }
      `}</style>
      </div>
    );
  });
